type LazyloadOptions = {
  src?: string;
  srcset?: string;
  selector?: string;
  root?: Element | Document | null;
  rootMargin?: string;
  threshold?: number;
};

/**
 * Native replacement for the `lazyload` npm package.
 * That UMD build breaks under Vite: CJS prebundle passes `exports` as `this`,
 * so `root` is undefined and `root.lazyload = ...` throws.
 */
export function initLazyload(
  images?: ArrayLike<Element> | null,
  options: LazyloadOptions = {}
): void {
  const settings = {
    src: 'data-src',
    srcset: 'data-srcset',
    selector: '.lazyload',
    root: null as Element | Document | null,
    rootMargin: '0px',
    threshold: 0,
    ...options
  };

  const elements = images || document.querySelectorAll(settings.selector);

  const applySource = (element: Element): void => {
    const el = element as HTMLElement & HTMLImageElement;
    const src = el.getAttribute(settings.src);
    const srcset = el.getAttribute(settings.srcset);

    if (el.tagName.toLowerCase() === 'img') {
      if (src) el.src = src;
      if (srcset) el.srcset = srcset;
      return;
    }

    if (src) {
      el.style.backgroundImage = `url('${src}')`;
    }
  };

  if (!('IntersectionObserver' in window)) {
    Array.prototype.forEach.call(elements, applySource);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      applySource(entry.target);
    });
  }, {
    root: settings.root,
    rootMargin: settings.rootMargin,
    threshold: [settings.threshold]
  });

  Array.prototype.forEach.call(elements, (element: Element) => {
    observer.observe(element);
  });
}

export default initLazyload;
